import React from 'react';
import UUID from '@ax2/common/model/UUID';
import styles from '@/styles/components/question-builders/base/Grid.module.css';
import DragList, { DragListProps } from '../../drag-and-drop/DragList';
import { removeById, fixOrder } from '@ax2/common/helpers/array';
import { Card } from 'components/drag-and-drop/DragCard';
import { AddActionProps } from '../../drag-and-drop/actions/AddAction';
import { RemoveActionProps } from '../../drag-and-drop/actions/RemoveAction';
import {
  TemplateContentElementData,
  TemplateContentElement,
} from './TemplateContentElement';
import _ from 'lodash';

export interface GridOption extends TemplateContentElementData {
  id: UUID;
  order: number;
}

export interface GridData {
  columns: GridOption[];
  rows: GridOption[];
  typeIcon?: string;
  editable: boolean;
  isLocked: boolean;
  showOrder: boolean;
  onNewColumn: () => void;
  onNewRow: () => void;
  onChangeColumns: (columns: GridOption[]) => void;
  onChangeRows: (rows: GridOption[]) => void;
}

const itemControl = (option: GridOption) => (
  <TemplateContentElement {...option} key={option.id} />
);

const orderControl = (order: number) => (
  <div className={styles.order}>{`${order + 1}.`}</div>
);

const isSectionLocked = (
  isGlobalLocked: boolean,
  sectionOptions: GridOption[]
) =>
  isGlobalLocked &&
  sectionOptions.length > 0 &&
  _.findIndex(sectionOptions, so => so.templateType === 'template') === -1;

const getDragListProps = (
  options: GridOption[],
  editable: boolean,
  showOrder: boolean,
  onChange: (opt: GridOption[]) => void,
  isLocked: boolean,
  addAction: AddActionProps,
  removeAction: RemoveActionProps<GridOption>,
  typeIcon?: string
): DragListProps<GridOption> => {
  const isSLocked = isSectionLocked(isLocked, options);
  const cards: Card<GridOption>[] = options.map(opt => ({
    itemControl: editable ? itemControl(opt) : undefined,
    orderControl: showOrder ? orderControl : undefined,
    dragZone: isSLocked ? 'none' : 'icon',
    typeIcon: typeIcon,
    itemFrame: 'border',
    isEditable: opt.templateType === 'template',
    item: opt,
  }));
  return {
    cards: cards,
    limit: 8,
    addAction: !isSLocked ? addAction : undefined,
    removeAction: !isSLocked ? removeAction : undefined,
    onComplete: data => onChange(fixOrder(data.map(d => d.item))),
  };
};

export const Grid: React.FC<GridData> = props => {
  return (
    <div className={styles.gridContainer}>
      <div className={styles.gridCol}>
        <span className={styles.label}>Column</span>
        <DragList
          {...getDragListProps(
            props.columns,
            props.editable,
            props.showOrder,
            props.onChangeColumns,
            props.isLocked,
            {
              onAdd: props.onNewColumn,
            },
            {
              onRemove: item => {
                const opts = removeById(props.columns, item.id);
                props.onChangeColumns(fixOrder(opts));
              },
            },
            props.typeIcon
          )}
        />
      </div>
      <div className={styles.gridCol}>
        <span className={styles.label}>Row</span>
        <DragList
          {...getDragListProps(
            props.rows,
            props.editable,
            props.showOrder,
            props.onChangeRows,
            props.isLocked,
            {
              onAdd: props.onNewRow,
            },
            {
              onRemove: item => {
                const opts = removeById(props.rows, item.id);
                props.onChangeRows(fixOrder(opts));
              },
            },
            props.typeIcon
          )}
        />
      </div>
    </div>
  );
};
